import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { registerServiceWorker } from './lib/pushNotifications';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import { ToastContainer } from './components/ui/Toast';
import InstallPrompt from './components/InstallPrompt';
import UpdateToast from './components/UpdateToast';
import PushPermissionPrompt from './components/PushPermissionPrompt';

// 관리자 페이지
import Auth from './pages/Auth';
import GymSetup from './pages/GymSetup';
import Dashboard from './pages/Dashboard';
import Members from './pages/Members';
import MemberDetail from './pages/MemberDetail';
import Memberships from './pages/Memberships';
import Attendance from './pages/Attendance';
import PT from './pages/PT';
import Trainers from './pages/Trainers';
import Lockers from './pages/Lockers';
import Revenue from './pages/Revenue';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Kiosk from './pages/Kiosk';
import Help from './pages/Help';

// 회원 포털
import MemberPortal from './pages/member/MemberPortal';

// 트레이너 포털
import TrainerPortal from './pages/trainer/TrainerPortal';

// 어드민 패널
import AdminPanel from './pages/admin/AdminPanel';
import AdminManageGym from './pages/admin/AdminManageGym';

// 공개 페이지
import Landing from './pages/Landing';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import KioskStandalone from './pages/KioskStandalone';

function Spinner() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400">불러오는 중...</p>
      </div>
    </div>
  );
}

// 헬스장 관리자 라우트
function GymOwnerRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/members" element={<Members />} />
        <Route path="/members/:id" element={<MemberDetail />} />
        <Route path="/memberships" element={<Memberships />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/pt" element={<PT />} />
        <Route path="/trainers" element={<Trainers />} />
        <Route path="/lockers" element={<Lockers />} />
        <Route path="/revenue" element={<Revenue />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/kiosk" element={<Kiosk />} />
        <Route path="/help" element={<Help />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function AppContent() {
  const { session, profile, gym, loading, isDemoMode, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // 공개 접근 가능 페이지 (로그인 불필요)
  if (location.pathname === '/terms') return <Terms />;
  if (location.pathname === '/privacy') return <Privacy />;
  if (location.pathname === '/kiosk') return <KioskStandalone />;

  // 인증 로딩 중
  if (loading && !isDemoMode) return <Spinner />;

  // 비로그인 분기
  if (!session && !isDemoMode) {
    if (location.pathname === '/login') return <Auth />;
    // 초대 코드가 URL에 있으면 Auth 페이지 바로 표시 (params 유지)
    if (searchParams.get('code') || searchParams.get('trainer_code')) return <Auth />;
    return <Landing onLogin={() => navigate('/login')} />;
  }

  // 역할 분기 (profile 로딩 완료 후)
  const role = profile?.role ?? 'gym_owner';

  if (role === 'admin') {
    return (
      <Routes>
        <Route path="/admin/gyms/:gymId/manage/*" element={<AdminManageGym />} />
        <Route path="*" element={<AdminPanel />} />
      </Routes>
    );
  }
  if (role === 'member') return <MemberPortal />;
  if (role === 'trainer') return <TrainerPortal />;

  // gym_owner 승인 대기 — 단, 24시간 임시 체험 기간 내라면 허용
  if (!isDemoMode && role === 'gym_owner' && profile?.status === 'pending') {
    const tempExpiry = gym?.plan_expires_at ? new Date(gym.plan_expires_at) : null;
    const withinTempWindow = tempExpiry && tempExpiry > new Date();
    if (!withinTempWindow) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
          <div className="w-full max-w-sm text-center">
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">승인 대기 중</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              24시간 무료 체험이 종료되었습니다.<br />관리자 승인 후 14일 체험이 시작됩니다.<br /><br />
              문의: <span className="text-primary-600">1877-1827</span>
            </p>
            <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto text-sm">
              로그아웃
            </button>
          </div>
        </div>
      );
    }
    // 24h 임시 체험 중 — fall through to dashboard
  }

  // gym_owner 정지
  if (!isDemoMode && role === 'gym_owner' && profile?.status === 'suspended') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">계정이 정지되었습니다</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            계정 이용이 제한되었습니다.<br />문의: <span className="text-primary-600">1877-1827</span>
          </p>
          <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto text-sm">
            로그아웃
          </button>
        </div>
      </div>
    );
  }

  // gym_owner: 헬스장 등록 안 됐으면 GymSetup
  if (!isDemoMode && !gym) return <GymSetup />;

  // 플랜 만료 차단
  if (!isDemoMode && gym?.plan_expires_at) {
    const expiresAt = new Date(gym.plan_expires_at);
    if (expiresAt < new Date()) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
          <div className="w-full max-w-sm text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">이용 플랜이 만료되었습니다</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              서비스 이용 기간이 만료되었습니다.<br />
              플랜을 갱신하려면 관리자에게 문의해주세요.<br /><br />
              문의: <span className="text-primary-600">1877-1827</span>
            </p>
            <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto text-sm">
              로그아웃
            </button>
          </div>
        </div>
      );
    }
  }

  // gym_owner + 데모 모드
  return <GymOwnerRoutes />;
}

function App() {
  useEffect(() => { registerServiceWorker(); }, []);

  return (
    <AuthProvider>
      <AppProvider>
        <Router>
          <AppContent />
          <ToastContainer />
          <InstallPrompt />
          <UpdateToast />
          <PushPermissionPrompt />
        </Router>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
